import { hasRegisteredUser } from '@sportworld/js-sdk/src/actions/user'
import { isDevMode, isEnv } from '@sportworld/js-sdk/src/envs/envs'

import {
  getAnonymousUserId,
  getRegisteredUserEmail,
  getRegisteredUserId,
  getRegisteredUserPlenigoId,
  getSwCountryCode,
} from '@/actions'
import {
  analyticsPageTypes,
  eventAction,
  eventObject,
  pageNamesWithPostfix,
  platformIds,
} from '@/constants/analytics'
import { YOUTUBE_VIDEO_ID_REGEXP, pageTypes, ytUrl } from '@/constants/common'
import { pathIds, paths } from '@/constants/paths'
import { getCurrentLanguage } from '@/i18n'
import { getDuid } from '@/platform/platform' // TODO check
import { platformName } from '@/platform/platformUtils' // TODO check
import { ApiService, PageService } from '@/service'
import useStore from '@/store/useStore'
import {
  AnalyticsEpgType,
  AnalyticsEventType,
  AnalyticsPageDataType,
  AnalyticsPageViewDataType,
  VideoViewedType,
} from '@/types/analyticsTypes'
import { EventType, eventTypes } from '@/types/servicesTypes/configTypes'
import {
  // findGtagPropertiesIds,
  findNearestByImgWidth,
  isTVOS,
  isTizen,
  isWebos,
  sendPageImpressionToGTAG,
  showInfo,
} from '@/utils/utils'

/**
 * @description
 * Debug events from iframe id="gtag-provider"
 */

/* const gtagIframeWindow = (window as { [key: string]: any })[
  'gtagIframeWindow'
] as any

window.addEventListener('message', (event) => {
  if (event.source === gtagIframeWindow) {
    isDevMode &&
      console.log(
        '[APP][Utils] Received message from GTAG iframe:',
        event?.data,
      )

    event?.data && findGtagPropertiesIds(event.data)

    try {
      showInfo(`response from gtag ${event.data}`)
    } catch (error) {}
  }
}) */

const gtmIframeWindow = (window as { [key: string]: any })[
  'gtmIframeWindow'
] as any

/**
 * @description
 * Debug an instrumented version of anLib.js
 */

/* 
window.addEventListener('message', (event) => {
  if (event.source === gtmIframeWindow) {
    isDevMode &&
      console.log('[APP][Utils] Received message from GTM iframe:', event?.data)

    try {
      showInfo(`response from gtm ${event?.data}`)
    } catch (error) {}
  } else {
    console.log('[APP][Utils] Received message:', event?.data)
  }
}) */

const getCalendarPageName = (number: number) => {
  if (number === 0) {
    return 'today'
  }
  if (number === 1) {
    return 'tomorrow'
  }
  return `plus-${number}-days`
}

export const getEpgObject = (event: EventType) => {
  const { allChannels } = useStore.getState().common
  const currentChannel = allChannels?.find(
    (channel) => channel.id === event.channelId,
  )

  const channelLogo = findNearestByImgWidth(
    currentChannel?.image?.sources,
    150,
    {
      prioritizeSvg: true,
    },
  )?.url

  const isYouTube = event.type === eventTypes.YOUTUBE
  const isChannel = event.type === eventTypes.CHANNEL

  let videoLink = event.video?.sources?.[0].url || ''

  if (isYouTube) {
    const youTubeVideoId = event.id?.replace(YOUTUBE_VIDEO_ID_REGEXP, '')
    const youTubeVideoLink = `${ytUrl}${youTubeVideoId}`
    videoLink = youTubeVideoLink
  }

  if (isChannel) {
    videoLink = currentChannel?.video?.sources?.[0]?.url || ''
  }

  const timestampData = isYouTube
    ? {
        publishedAt: event.startTime,
      }
    : {
        timestampStart: event.startTime,
        timestampEnd: event.endTime,
      }

  const ppvIds = event.ppvInfo?.map(({ id }) => id)

  const epg: AnalyticsEpgType = {
    id: event.id,
    title: event.name,
    isLive: event.live,
    channelId: event.channelId,
    channelName: currentChannel?.name,
    channelLogo: channelLogo,
    type: 'sport',
    sport: event.categoryId,
    videoLink: videoLink,
    partnerIds: event.partnerIds,
    ...timestampData,
    ppv: Boolean(event.ppvInfo),
    ...(ppvIds && { ppvIds }),
  }

  return epg
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const showAnalyticsInfo = (data: any) => {
  switch (data?.object) {
    case eventObject.APP:
      showInfo(
        `${data?.object} ${data?.action} ${data.app_version} ${data.mode}`,
      )
      break
    case eventObject.PAGE:
      showInfo(`${data?.object} ${data?.action}
      - id: ${data?.page?.id},
      - name: ${data?.page?.name}, ${
        data?.page?.type ? '\n- type: ' + data?.page?.type : ''
      } ${data?.page?.sitemap ? '\n- sitemap: ' + data?.page?.sitemap : ''} ${
        data?.page?.sport ? '\n- sport: ' + data?.page?.sport : ''
      }`)
      break
    case eventObject.VIDEO:
      if (data?.video?.milestone) {
        showInfo(`${data?.object} ${data?.action} ${data.video.milestone}`)
      } else {
        showInfo(`${data?.object} ${data?.action}`)
      }
      break
    default:
      showInfo(`${data?.object} ${data?.action}`)
      break
  }
}

const getPageAnalyticsDataByPageId = async (pageId?: string) => {
  const pageData: Partial<AnalyticsPageDataType> = {}
  if (!pageId) {
    return pageData
  }

  pageData.id = pageId
  const { pagePopup } = useStore.getState().popup
  const { page } = useStore.getState().page
  const currentPage = pagePopup || page

  try {
    const page =
      currentPage?.id === pageId
        ? currentPage
        : await PageService.getPageById(pageId)

    pageData.partnerIds = page.partnerIds

    const categoryId = page.categoryId
    if (categoryId) {
      pageData.sport = categoryId
    }

    const isTabsPageType = page.pageType === pageTypes.TABS
    const isHomeTabPageType = page.pageType === pageTypes.HOME_TAB

    const analyticsPageType = isHomeTabPageType
      ? analyticsPageTypes.HOME_TAB
      : isTabsPageType
      ? analyticsPageTypes.CONTENT_HUB
      : categoryId
      ? analyticsPageTypes.CATEGORY
      : analyticsPageTypes.OVERVIEW

    let pageName = page.name || page.id

    if (isTabsPageType) {
      const currentTab = page.tabs?.[page.tabIndex || 0]
      pageName = currentTab?.name || currentTab?.title || ''
    }
    if (isHomeTabPageType) {
      const currentTab = page.homeTab?.[page.tabIndex || 0]
      pageName = currentTab?.title || ''
    }

    pageData.type = analyticsPageType
    pageData.name = pageName

    switch (analyticsPageType) {
      case analyticsPageTypes.CONTENT_HUB:
        pageData.contentHubName = page.name || page.id
        pageData.sitemap = 'content-hubs'
        break

      case analyticsPageTypes.CATEGORY:
        pageData.name = `${categoryId}-home`
        pageData.sitemap = `sports::${categoryId}`
        break

      default:
        pageData.sitemap = analyticsPageType
        break
    }
  } catch {}

  return pageData
}

const getPageDataByEventId = async (eventId?: string) => {
  const pageData: Partial<AnalyticsPageDataType> = {}
  if (!eventId) {
    return pageData
  }

  pageData.id = eventId
  pageData.type = analyticsPageTypes.EPG_DETAILS
  const { currentEvent } = useStore.getState().common

  try {
    const event =
      currentEvent?.id === eventId
        ? currentEvent
        : await ApiService.getEventById(eventId)
    if (event) {
      pageData.name = event.name
      pageData.sport = event.categoryId
      pageData.sitemap = `sports::${event.categoryId}::details`

      const epg = getEpgObject(event)
      pageData.epg = epg
      pageData.partnerIds = event.partnerIds
    }
  } catch {}

  return pageData
}

export const sendAnalyticsEvent = async (data: AnalyticsEventType) => {
  if (!isTVOS() && isEnv('prod')) {
    isDevMode && console.log('🐥 GTM (disabled)', data)
    return
  }

  // isDevMode && console.log('🐥 GTM', data)

  // const { appSettings } = useStore.getState().common
  // appSettings.isDevToolsUsed && isDevMode && showAnalyticsInfo(data)
  try {
    gtmIframeWindow.postMessage(data, '*')
  } catch (error) {}
}

export const getAnalyticsUserData = () => {
  const deviceId = getDuid()
  const isRegisteredUser = hasRegisteredUser()
  const loggedIn = isRegisteredUser ? 'yes' : 'no'
  const email = getRegisteredUserEmail()
  const plenigoId = getRegisteredUserPlenigoId()
  const userId = isRegisteredUser ? getRegisteredUserId() : getAnonymousUserId()

  return {
    loggedIn,
    ...(email && { email }),
    ...(plenigoId && { plenigoId }),
    userId,
    deviceId,
  }
}

export const sendPageViewEvent = async (pathname: string) => {
  const ignoredPaths = [paths.ROOT, paths.INDEX, paths.EMPTY]

  if (ignoredPaths.includes(pathname)) {
    return false
  }

  const regionCode = getSwCountryCode()
  const language = getCurrentLanguage()
  const platformId = isTizen()
    ? platformIds.CTV_SAMSUNG
    : isWebos()
    ? platformIds.CTV_LG
    : platformName
  const platformVersion = import.meta.env.REACT_APP_VERSION || ''
  const user = getAnalyticsUserData()

  const pageViewAnalyticsData: AnalyticsPageViewDataType = {
    platform: {
      version: platformVersion,
      id: platformId,
    },

    page: {
      country: regionCode,
      language,
      url: {
        href: window.location.href,
      },
    },

    user,
  }

  await modifyAnalyticsPageData(pathname, pageViewAnalyticsData)

  sendAnalyticsEvent({
    object: eventObject.PAGE,
    action: eventAction.VIEW,
    ...pageViewAnalyticsData,
  })
}

const modifyAnalyticsPageData = async (
  pathname: string,
  pageViewAnalyticsData: AnalyticsPageViewDataType,
) => {
  const analyticsPageData = pageViewAnalyticsData.page

  switch (true) {
    case pathname.includes(paths.PAGE) ||
      (pathname.includes(paths.FAVORITES) && pathname !== paths.FAVORITES): {
      const pageId = pathname.split(paths.ROOT).pop()
      const pageData = await getPageAnalyticsDataByPageId(pageId)

      pageViewAnalyticsData.page = { ...analyticsPageData, ...pageData }
      break
    }

    case pathname.includes(paths.LIST): {
      const listProps = pathname.split(paths.ROOT)

      analyticsPageData.name = listProps.pop()
      analyticsPageData.id = listProps.pop()
      analyticsPageData.type = analyticsPageTypes.OVERVIEW
      break
    }

    case pathname.includes(paths.EVENT_PAGE): {
      const eventId = pathname.split(paths.ROOT).pop()
      const eventData = await getPageDataByEventId(eventId)

      pageViewAnalyticsData.page = { ...analyticsPageData, ...eventData }
      break
    }

    default: {
      const pageId = pathIds[pathname as keyof object]
      const pageIdFromPath = pathname.substring(1)
      const rootPath = pathname.split(paths.ROOT)[1]

      analyticsPageData.id = pageId || pageIdFromPath
      analyticsPageData.type =
        analyticsPageTypes[rootPath.toUpperCase() as keyof object] ||
        analyticsPageTypes.OTHER
      analyticsPageData.sitemap =
        pathIds[(paths.ROOT + rootPath) as keyof object] || pageIdFromPath

      if (pathname.includes(paths.CLASSIC_HOME)) {
        const { page } = useStore.getState().page
        // analyticsPageData.id = page?.id || pageId || pageIdFromPath
        const currentTab = page?.homeTab?.[page.tabIndex || 0]
        analyticsPageData.name = currentTab?.title || ''
        break
      }

      if (pathname.includes(paths.FEATURE_SCREEN)) {
        const featureScreenId = pathname.split(paths.ROOT)[2]
        analyticsPageData.id = featureScreenId
        analyticsPageData.name = featureScreenId
        analyticsPageData.type = analyticsPageTypes.FEATURE_SCREEN
        break
      }

      if (pathname.includes(paths.CALENDAR)) {
        const { calendarIndex } = useStore.getState().common
        const pageName = `${pageId}-${getCalendarPageName(calendarIndex)}`
        analyticsPageData.name = pageName
        analyticsPageData.id = pageName
      } else if (pageId) {
        const pageName =
          pageId in pageNamesWithPostfix && pageNamesWithPostfix[pageId]
        analyticsPageData.name = pageName || pageId
      } else {
        analyticsPageData.name = pageIdFromPath
      }
      break
    }
  }
}

export const handlePageViewAnalyticsEvent = (pathname: string) => {
  const { listPopup, mediaVideo, currentEvent } = useStore.getState().common
  const { pagePopup } = useStore.getState().popup

  const path = currentEvent
    ? `${paths.EVENT_PAGE}/${currentEvent.id}`
    : pagePopup
    ? `${paths.PAGE}/${pagePopup.id}`
    : pathname

  if (listPopup) {
    sendPageViewEvent(path)
    sendPageImpressionToGTAG(path)
  }

  if (mediaVideo) {
    const updatedPath = mediaVideo.listOptions
      ? `${paths.LIST}/${mediaVideo.listOptions.listContextId}/${mediaVideo.listOptions.name}`
      : path
    sendPageImpressionToGTAG(updatedPath)
  }
}

export const sendMilestoneSecAnalyticsEvent = (videoData: VideoViewedType) => {
  sendAnalyticsEvent({
    object: eventObject.VIDEO,
    action: eventAction.MILESTONE_10SEC,
    video: {
      id: videoData.id,
      link: videoData.url,
    },
  })
}

export const sendMilestonePercentAnalyticsEvent = (
  milestone: number,
  videoData: VideoViewedType,
) => {
  sendAnalyticsEvent({
    object: eventObject.VIDEO,
    action: eventAction.MILESTONE_PERCENT,
    video: {
      id: videoData.id,
      link: videoData.url,
      milestone: milestone,
    },
  })
}

export const getPercentageOfViewed = (
  duration?: number,
  currentTime?: number,
) => {
  const videoDuration = Math.floor(duration || 0)
  const videoCurrentTime = Math.ceil(currentTime || 0)
  const percentageFull =
    videoDuration && (videoCurrentTime / videoDuration) * 100
  const percentage = Math.floor(percentageFull)
  return percentage
}
